import React, { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import translations from '../../constants/translations.json';
import configData from '../../config.json';
import { useDataContext } from '../../context/data';
import { useStampWallet } from '../../context/stampWallet';
import { isVoucherReady, READY_STATUSES } from '../../utils/stampcard';
import QrImage from '../Staff/QrImage';

// Identity bearer for wallet-scoped calls, ACCOUNT-FIRST (same priority as the
// stampWallet context): a logged-in profile token wins over a lingering guest
// wallet token so the pass always identifies the richer wallet.
const readIdentityToken = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('user_profile') || 'null');
    if (profile?.token) return profile.token;
    return localStorage.getItem('hw-wallet-token') || '';
  } catch (e) {
    return '';
  }
};

// Tabs per §9.4
const TABS = [
  { key: 'progress', labelKey: 'wallet_tab_progress', fallback: 'In progress' },
  { key: 'ready', labelKey: 'wallet_tab_ready', fallback: 'Ready' },
  { key: 'history', labelKey: 'wallet_tab_history', fallback: 'History' },
];

// Voucher status buckets. READY_STATUSES + the expiry rule live in the wallet
// context so the cart applies exactly the same test.
const HISTORY_STATUSES = ['redeemed', 'expired', 'revoked'];

// "Show my card to staff" — the §6.4a identity pass. Fetches a short-lived
// server-minted pass code (GET /guest/wallet/pass) and renders it as a QR the
// staff tool scans to credit a cash-sale stamp (STAFF_GRANT). Auto-renews just
// before expiry while open, so the QR at the counter never goes stale.
function WalletPassCard({ t }) {
  const [open, setOpen] = useState(false);
  const [pass, setPass] = useState('');
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState(false);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((id) => clearInterval(id));
    timers.current = [];
  };

  const fetchPass = async () => {
    setError(false);
    try {
      const token = readIdentityToken();
      const res = await axios.get(`${configData.SERVER_URL}/guest/wallet/pass`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const p = res?.data?.pass || '';
      const ttl = Number(res?.data?.expiresInSec) > 0 ? Number(res.data.expiresInSec) : 120;
      if (!p) {
        setError(true);
        return;
      }
      setPass(p);
      setRemaining(ttl);
    } catch (e) {
      setError(true);
    }
  };

  // While open: 1s countdown; renew 10s before expiry so the shown code is
  // always accepted by the grant.
  useEffect(() => {
    clearTimers();
    if (!open) {
      setPass('');
      return undefined;
    }
    fetchPass();
    const tick = setInterval(() => {
      setRemaining((r) => {
        if (r <= 11) {
          fetchPass();
          return r;
        }
        return r - 1;
      });
    }, 1000);
    timers.current.push(tick);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 14,
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 24, flexShrink: 0 }}>💳</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>
            {t('wallet_pass_title', 'Earning at the counter?')}
          </p>
          <p style={{ fontSize: 12, color: '#999', margin: '2px 0 0' }}>
            {t('wallet_pass_desc', 'Show your card to staff to collect stamps on a cash sale')}
          </p>
        </div>
        <i
          className={`fa fa-chevron-${open ? 'up' : 'down'}`}
          style={{ color: '#999', fontSize: 13, flexShrink: 0 }}
        />
      </button>

      {open && (
        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            background: '#fafafa',
            padding: '20px 18px',
            textAlign: 'center',
          }}
        >
          {error ? (
            <>
              <p style={{ fontSize: 13, color: '#c62828', margin: '0 0 10px' }}>
                {t('wallet_pass_error', "Couldn't fetch your card pass. Are you online?")}
              </p>
              <button
                onClick={fetchPass}
                style={{
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('wallet_pass_retry', 'Try again')}
              </button>
            </>
          ) : pass ? (
            <>
              <div
                style={{
                  width: 176,
                  margin: '0 auto',
                  background: '#fff',
                  borderRadius: 12,
                  padding: 8,
                  border: '1px solid #e5e7eb',
                }}
              >
                <QrImage value={pass} size={160} />
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: '#555',
                  margin: '12px 0 0',
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                {pass}
              </p>
              <p style={{ fontSize: 11, color: '#bbb', margin: '6px 0 0' }}>
                {t('wallet_pass_hint', 'Staff scan this to add your stamp')} · {remaining}s
              </p>
            </>
          ) : (
            <p style={{ fontSize: 13, color: '#999', margin: 0 }}>{t('loading', 'Loading...')}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Wallet() {
  const navigate = useNavigate();
  const { language } = useDataContext();

  // The wallet context (§9.1). Consumed defensively — if the provider is not
  // mounted yet, or there's no Version:2 data, everything fails safe to empty.
  const wallet = useStampWallet() || {};
  const walletStamps = Array.isArray(wallet.walletStamps) ? wallet.walletStamps : [];
  const vouchers = Array.isArray(wallet.vouchers) ? wallet.vouchers : [];
  const walletOwnerToken = wallet.walletOwnerToken || null;
  const fetchWallet = wallet.fetchWallet;
  const stageVoucher = wallet.stageVoucher;
  const walletLoading = wallet.loading === true;

  const [activeTab, setActiveTab] = useState('progress');
  const [stagedCode, setStagedCode] = useState(null); // local "staged at checkout" echo
  const [qrVoucher, setQrVoucher] = useState(null); // voucher whose QR pass is shown

  const t = (key, fallback) => translations[language]?.[key] || fallback;

  // Logged-in vs guest-with-token (§9.4). A signed wallet token OR a user
  // profile means there's a wallet to show.
  const userProfile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_profile'));
    } catch {
      return null;
    }
  }, []);
  const isLoggedIn = !!userProfile?.profile;
  const hasWalletToken = !!walletOwnerToken || !!localStorage.getItem('hw-wallet-token');
  const isGuestWithProgress = !isLoggedIn && hasWalletToken;

  // Pull the wallet on mount. fetchWallet is identity-scoped via the header
  // token inside the provider (never a query param, A4).
  useEffect(() => {
    if (typeof fetchWallet === 'function') {
      try {
        fetchWallet();
      } catch (e) {
        /* fail-safe-to-nothing */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derive the three buckets ───────────────────────────────────────────────
  // "In progress" = cards with currentCount/maxCount (and not yet complete).
  const inProgress = useMemo(() => {
    return walletStamps.filter((s) => {
      const cur = Number(s?.currentCount || 0);
      const max = Number(s?.maxCount || 0);
      return max > 0; // a real card
    });
  }, [walletStamps]);

  const readyVouchers = useMemo(() => vouchers.filter(isVoucherReady), [vouchers]);

  console.log('vouchers', vouchers);
  console.log('walletStamps', walletStamps);

  const historyVouchers = useMemo(
    () =>
      vouchers.filter((v) => {
        const status = (v?.Status || '').toLowerCase();
        if (HISTORY_STATUSES.includes(status)) return true;
        // Still 'issued' but past its expiry: History, not Ready. Without this it
        // would fall out of Ready and land in no tab at all.
        return READY_STATUSES.includes(status) && !isVoucherReady(v);
      }),
    [vouchers],
  );

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleStage = (voucher) => {
    // Pre-stage for in-cart redemption (§9.3). The reward product chooser lives
    // in the cart; here we stage the whole voucher and the snapshot's first
    // eligible product as a sensible default (server is authoritative).
    const snapshot = voucher?.RewardSnapshot || {};
    const defaultProductId =
      (Array.isArray(snapshot.EligibleProductIds) && snapshot.EligibleProductIds[0]) || null;
    if (typeof stageVoucher === 'function') {
      try {
        stageVoucher(voucher, defaultProductId);
      } catch (e) {
        /* no-op */
      }
    }
    setStagedCode(voucher?.Code || voucher?._id || null);
  };

  // One-click redeem: jump straight to the voucher's restaurant with a cart of
  // just the reward product and head to checkout. The menu page reads the intent
  // (location.state.stampRedeem), sets the cart, stages the voucher and opens
  // review. Falls back to plain staging if we can't resolve restaurant/product.
  const handleRedeemNow = (voucher) => {
    const snapshot = voucher?.RewardSnapshot || {};
    const productId =
      (Array.isArray(snapshot.EligibleProductIds) && snapshot.EligibleProductIds[0]) || null;
    const rid =
      voucher?.Restaurant?._id ||
      voucher?.Restaurant ||
      (Array.isArray(voucher?.RedeemableAt) && voucher.RedeemableAt[0]) ||
      null;
    if (!rid || !productId) {
      handleStage(voucher);
      return;
    }
    navigate(`/restaurants/${rid}/menu`, {
      state: {
        stampRedeem: { code: voucher?.Code || voucher?.code, productId: String(productId) },
      },
    });
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const rewardName = (voucher) => {
    const snap = voucher?.RewardSnapshot || {};
    return (
      voucher?.rewardName ||
      snap.RewardName ||
      voucher?.reward?.name ||
      voucher?.CampaignName ||
      voucher?.Campaign?.name ||
      voucher?.campaign?.name ||
      t('wallet_reward_generic', 'Free reward')
    );
  };

  const restaurantName = (voucher) =>
    voucher?.restaurantName ||
    voucher?.Restaurant?.name ||
    voucher?.Restaurant?.RestaurantName ||
    voucher?.Restaurant?.Name ||
    voucher?.restaurant?.name ||
    '';

  const expiresLabel = (voucher) => {
    if (!voucher?.ExpiresAt) return null;
    return `${t('wallet_expires', 'Expires')} ${moment(voucher.ExpiresAt).format('D. MMM YYYY')}`;
  };

  const statusBadge = (voucher) => {
    const s = (voucher?.Status || '').toLowerCase();
    const expired = {
      bg: '#f3f4f6',
      color: '#6b7280',
      label: t('wallet_status_expired', 'Expired'),
    };
    if (s === 'redeemed')
      return { bg: '#dcfce7', color: '#166534', label: t('wallet_status_redeemed', 'Redeemed') };
    if (s === 'expired') return expired;
    if (s === 'revoked')
      return { bg: '#fce4ec', color: '#c62828', label: t('wallet_status_revoked', 'Revoked') };
    // Still 'issued'/'reserved' but lapsed — read as Expired, matching its bucket.
    if (!isVoucherReady(voucher)) return expired;
    if (s === 'reserved')
      return { bg: '#fff7ed', color: '#c2410c', label: t('wallet_status_reserved', 'Reserved') };
    return { bg: '#e0f2fe', color: '#0369a1', label: t('wallet_status_ready', 'Ready') };
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const emptyState = (icon, title, subtitle) => (
    <div
      style={{
        textAlign: 'center',
        padding: '60px 20px',
        background: 'white',
        borderRadius: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{icon}</span>
      <p style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 4 }}>{title}</p>
      <p style={{ fontSize: 13, color: '#999' }}>{subtitle}</p>
    </div>
  );

  const renderProgressCard = (card, i) => {
    const cur = Number(card?.currentCount || 0);
    const max = Number(card?.maxCount || 0);
    const pct = max > 0 ? Math.min(100, Math.round((cur / max) * 100)) : 0;
    const name = card?.name || card?.reward?.name || t('wallet_card_generic', 'Stamp card');
    const rest =
      card?.restaurants?.[0]?.name ||
      card?.restaurants?.[0]?.RestaurantName ||
      card?.restaurantName ||
      '';
    const dots = Array.from({ length: Math.min(max, 12) });

    return (
      <div
        key={card?._id || i}
        style={{
          background: 'white',
          borderRadius: 14,
          padding: '16px 18px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 10,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#1a1a1a',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </p>
            {rest && <p style={{ fontSize: 12, color: '#999', margin: '2px 0 0' }}>{rest}</p>}
          </div>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#96c11f',
              flexShrink: 0,
              marginLeft: 12,
            }}
          >
            {cur} / {max}
          </span>
        </div>

        {/* Stamp dots */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {dots.map((_, di) => (
            <span
              key={di}
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: di < cur ? '#96c11f' : '#eef0f2',
                border: di < cur ? '1px solid #7ea818' : '1px solid #e2e5e8',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                color: '#fff',
              }}
            >
              {di < cur ? '✓' : ''}
            </span>
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ height: 6, background: '#eef0f2', borderRadius: 3, overflow: 'hidden' }}>
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: '#96c11f',
              transition: 'width 0.3s',
            }}
          />
        </div>
        <p style={{ fontSize: 12, color: '#999', margin: '8px 0 0' }}>
          {max - cur > 0
            ? `${max - cur} ${t('wallet_stamps_to_reward', 'more to your reward')}`
            : t('wallet_reward_unlocked', 'Reward unlocked!')}
        </p>
      </div>
    );
  };

  const renderReadyCard = (voucher, i) => {
    const code = voucher?.Code || voucher?._id || i;
    const isStaged = stagedCode && (stagedCode === voucher?.Code || stagedCode === voucher?._id);
    const badge = statusBadge(voucher);
    const rest = restaurantName(voucher);
    const exp = expiresLabel(voucher);

    return (
      <div
        key={code}
        style={{
          background: 'white',
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ padding: '16px 18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 8,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>🎁</span>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>
                  {rewardName(voucher)}
                </p>
              </div>
              {rest && (
                <p style={{ fontSize: 12, color: '#999', margin: '2px 0 0 24px' }}>{rest}</p>
              )}
            </div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 6,
                flexShrink: 0,
                background: badge.bg,
                color: badge.color,
              }}
            >
              {badge.label}
            </span>
          </div>

          {exp && (
            <p style={{ fontSize: 12, color: '#c2410c', margin: '6px 0 0 24px', fontWeight: 500 }}>
              ⏳ {exp}
            </p>
          )}

          {/* Affordances */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              onClick={() => handleRedeemNow(voucher)}
              style={{
                flex: 1,
                minWidth: 140,
                padding: '10px 14px',
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                background: '#96c11f',
                color: '#fff',
                transition: 'all 0.15s',
              }}
            >
              {t('wallet_redeem_now', 'Redeem now')}
            </button>

            <button
              onClick={() => setQrVoucher(qrVoucher === code ? null : code)}
              style={{
                flex: 1,
                minWidth: 140,
                padding: '10px 14px',
                borderRadius: 10,
                border: '1px solid #e5e7eb',
                background: 'white',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: '#555',
              }}
            >
              <i className="fa fa-qrcode" style={{ marginRight: 6 }} />
              {t('wallet_show_qr', 'Show QR to staff')}
            </button>
          </div>
        </div>

        {/* In-person burn pass (§6.4): the QR encodes the voucher CODE — staff
            scan (or type) it into the staff tool, which burns via the
            staff-authenticated burn-by-code path. No expiry dance at the
            counter: the code lives as long as the voucher does. */}
        {qrVoucher === code && (
          <div
            style={{
              borderTop: '1px solid #f0f0f0',
              background: '#fafafa',
              padding: '20px 18px',
              textAlign: 'center',
            }}
          >
            {voucher?.Code ? (
              <>
                <div
                  style={{
                    width: 176,
                    margin: '0 auto',
                    background: '#fff',
                    borderRadius: 12,
                    padding: 8,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <QrImage value={voucher.Code} size={160} />
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: '#555',
                    margin: '12px 0 0',
                    letterSpacing: 2,
                    fontWeight: 600,
                  }}
                >
                  {voucher.Code}
                </p>
              </>
            ) : null}
            <p style={{ fontSize: 11, color: '#bbb', margin: '6px 0 0' }}>
              {t('wallet_qr_hint', 'Show this to staff to redeem in person')}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderHistoryCard = (voucher, i) => {
    const badge = statusBadge(voucher);
    const rest = restaurantName(voucher);
    const when = voucher?.RedeemedAt || voucher?.ExpiresAt;

    return (
      <div
        key={voucher?._id || i}
        style={{
          background: 'white',
          borderRadius: 14,
          padding: '14px 18px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          opacity: 0.85,
        }}
      >
        <span style={{ fontSize: 22, flexShrink: 0 }}>
          {(voucher?.Status || '').toLowerCase() === 'redeemed' ? '✅' : '⌛'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#444', margin: 0 }}>
            {rewardName(voucher)}
          </p>
          <p style={{ fontSize: 12, color: '#aaa', margin: '2px 0 0' }}>
            {rest && `${rest} · `}
            {when ? moment(when).format('D. MMM YYYY') : ''}
          </p>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 6,
            flexShrink: 0,
            background: badge.bg,
            color: badge.color,
          }}
        >
          {badge.label}
        </span>
      </div>
    );
  };

  // ── Tab content ──────────────────────────────────────────────────────────
  const renderTab = () => {
    if (walletLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{ fontSize: 24, marginBottom: 12, display: 'block' }}
          />
          {t('loading', 'Loading...')}
        </div>
      );
    }

    if (activeTab === 'progress') {
      if (inProgress.length === 0) {
        return emptyState(
          '🎟️',
          t('wallet_empty_progress_title', 'No stamp cards yet'),
          t(
            'wallet_empty_progress_desc',
            'Order at a participating restaurant to start collecting',
          ),
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {inProgress.map(renderProgressCard)}
        </div>
      );
    }

    if (activeTab === 'ready') {
      if (readyVouchers.length === 0) {
        return emptyState(
          '🎁',
          t('wallet_empty_ready_title', 'No rewards ready'),
          t('wallet_empty_ready_desc', 'Completed cards turn into rewards you can redeem here'),
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {readyVouchers.map(renderReadyCard)}
        </div>
      );
    }

    // history
    if (historyVouchers.length === 0) {
      return emptyState(
        '📜',
        t('wallet_empty_history_title', 'No history yet'),
        t('wallet_empty_history_desc', 'Redeemed and expired rewards show up here'),
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {historyVouchers.map(renderHistoryCard)}
      </div>
    );
  };

  const tabCount = (key) => {
    if (key === 'progress') return inProgress.length;
    if (key === 'ready') return readyVouchers.length;
    if (key === 'history') return historyVouchers.length;
    return 0;
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
          {t('wallet_title', 'My wallet')}
        </h2>
        <p style={{ fontSize: 14, color: '#888' }}>
          {t('wallet_subtitle', 'Your stamp cards and rewards')}
        </p>
      </div>

      {/* Conversion-ramp banner for guests with progress (§9.4) — display only */}
      {isGuestWithProgress && (inProgress.length > 0 || readyVouchers.length > 0) && (
        <div
          onClick={() => navigate('/register')}
          style={{
            background: 'linear-gradient(135deg, #f0f9e8 0%, #e7f5d8 100%)',
            border: '1px solid #c9e6a8',
            borderRadius: 14,
            padding: '14px 18px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 26, flexShrink: 0 }}>📲</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#3f6212', margin: 0 }}>
              {t('wallet_convert_title', 'Save your card')}
            </p>
            <p style={{ fontSize: 12, color: '#557024', margin: '2px 0 0' }}>
              {t('wallet_convert_desc', 'Create an account so it follows you to any device')}
            </p>
          </div>
          <i
            className="fa fa-chevron-right"
            style={{ color: '#7ea818', fontSize: 14, flexShrink: 0 }}
          />
        </div>
      )}

      {/* Identity pass (§6.4a) — only when there IS a wallet identity to present */}
      {(isLoggedIn || hasWalletToken) && <WalletPassCard t={t} />}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setQrVoucher(null);
            }}
            style={{
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: activeTab === tab.key ? '#1a1a1a' : '#999',
              borderBottom: activeTab === tab.key ? '2px solid #96c11f' : '2px solid transparent',
              marginBottom: -1,
              transition: 'all 0.15s',
            }}
          >
            {t(tab.labelKey, tab.fallback)}
            {tabCount(tab.key) > 0 && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 10,
                  background: activeTab === tab.key ? '#96c11f' : '#eef0f2',
                  color: activeTab === tab.key ? '#fff' : '#999',
                }}
              >
                {tabCount(tab.key)}
              </span>
            )}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
}
